import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastStatus } from '@/components/domain/shared/ToastStatus';
import { usePermissions } from '@/hooks/usePermissions';
import { useEmailStatusFeed } from '@/hooks/useEmailStatusFeed';
import { useEmailQuota } from '@/hooks/useEmailQuota';
import { useMonthlyEmailsReviewed } from '@/hooks/useMonthlyEmailsReviewed';
import { emailService } from '@/services';
import { triggerProcessing } from '@/services/email.service';
import type { EmailStatusFeedRow } from '@/services/email.service';
import { useBottomToastStore } from '@/stores/bottomToastStore';
import {
  selectGmailBannerVisible,
  useVisibleBannersStore,
} from '@/stores/visibleBannersStore';
import { isReadingStatus } from '@/lib/emailReadState';
import {
  ACTION_LABELS,
  computeStatusToast,
  STILL_READING_AFTER_MS,
  type RunAction,
} from '@/lib/emailStatusRun';
import type { ReviewHighlightState } from '@/lib/reviewHighlight';

/** The status lane id this controller owns in the shared bottom slot. */
const EMAIL_STATUS_TOAST_ID = 'email-status';

/**
 * An email is "seen" per device once the parent closes its toast, acts on it
 * or lets a result time out — keyed by status and failure count, so the same
 * email comes back when it changes (a retry, a second failure). YAR-480
 * leaves cross-device "seen" open; per device is the conservative answer.
 */
function signature(e: Pick<EmailStatusFeedRow, 'status' | 'failureCount'>) {
  return `${e.status}:${e.failureCount}`;
}

function ackKey(householdId: string) {
  return `yaruta.emailStatus.seen.${householdId}`;
}

function loadAcks(householdId: string): Record<string, string> {
  try {
    const raw = localStorage.getItem(ackKey(householdId));
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, string>)
      : {};
  } catch {
    return {};
  }
}

function saveAcks(householdId: string, acks: Record<string, string>) {
  try {
    localStorage.setItem(ackKey(householdId), JSON.stringify(acks));
  } catch {
    // Private mode / quota: the toast just may show again next session.
  }
}

export interface EmailStatusToastProps {
  householdId: string | null | undefined;
  /** Forwarded to the feed: re-count Review and the plan meter. */
  onEmailsChanged?: () => void;
  /** Bumps re-read the plan usage for the Plan limit state. */
  refreshKey?: number;
}

/**
 * Drives the email status toast (YAR-477) through the shared bottom slot's
 * status lane. Renders nothing itself — `BottomToastSlot` owns placement,
 * the visible-time clock (Found / Nothing found: 6 s), motion, the polite
 * announcement, and yields to the undo toast.
 *
 * - Audience: Admin and Co-admin only (`canReview`, YAR-171 AC8).
 * - No toast while a `gmail_*` system banner shows.
 * - Buttons, not a tappable card: Review / See email(s) open Review at the
 *   top with the run's cards highlighted; Try again re-reads only the failed
 *   emails and disappears while it runs; Upgrade plan opens the plan page.
 */
export function EmailStatusToast({
  householdId,
  onEmailsChanged,
  refreshKey,
}: EmailStatusToastProps) {
  const navigate = useNavigate();
  const { canReview, resolved } = usePermissions();
  const gmailBanner = useVisibleBannersStore(selectGmailBannerVisible);
  const showStatus = useBottomToastStore((s) => s.showStatus);
  const dismissStatus = useBottomToastStore((s) => s.dismissStatus);

  const { emails, refresh } = useEmailStatusFeed(householdId, {
    onEmailsChanged,
    // Editors and Viewers never see the toast: no idle polling for them.
    idlePoll: resolved && canReview,
  });

  const quota = useEmailQuota(householdId, refreshKey);
  const used = useMonthlyEmailsReviewed(householdId, refreshKey);
  const quotaReached =
    typeof quota === 'number' && typeof used === 'number' && used >= quota;

  const [acks, setAcks] = useState<Record<string, string>>({});
  useEffect(() => {
    setAcks(householdId ? loadAcks(householdId) : {});
  }, [householdId]);

  // Try again: ids the parent re-sent this session (they rejoin the run
  // whatever their arrival time) and the ones still in flight.
  const [retriedIds, setRetriedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [retrying, setRetrying] = useState(false);

  // The Still reading wording needs a clock while anything is reading.
  const [now, setNow] = useState(() => Date.now());
  const anyReading = emails.some((e) => isReadingStatus(e.status));
  useEffect(() => {
    setNow(Date.now());
    if (!anyReading) return;
    const interval = setInterval(
      () => setNow(Date.now()),
      STILL_READING_AFTER_MS / 4,
    );
    return () => clearInterval(interval);
  }, [anyReading, emails]);

  const candidates = useMemo(
    () => emails.filter((e) => acks[e.id] !== signature(e)),
    [emails, acks],
  );
  const carriedIds = useMemo(
    () =>
      candidates
        .filter((e) => e.status === 'ERROR' || retriedIds.has(e.id))
        .map((e) => e.id),
    [candidates, retriedIds],
  );
  const model = useMemo(
    () =>
      computeStatusToast({
        emails: candidates,
        now,
        quotaReached,
        carriedIds,
      }),
    [candidates, now, quotaReached, carriedIds],
  );

  const latest = useRef({ model, emails, householdId });
  latest.current = { model, emails, householdId };

  /** Mark the current run seen, so it does not come back on this device. */
  const acknowledge = useCallback(() => {
    const { model: m, emails: rows, householdId: hh } = latest.current;
    if (!m || !hh) return;
    setAcks((prev) => {
      const next = { ...prev };
      for (const id of m.emailIds) {
        const row = rows.find((r) => r.id === id);
        if (row) next[id] = signature(row);
      }
      // Forget rows that left the feed, so the store cannot grow forever.
      for (const id of Object.keys(next)) {
        if (!rows.some((r) => r.id === id)) delete next[id];
      }
      saveAcks(hh, next);
      return next;
    });
  }, []);

  const openReview = useCallback(
    (ids: string[]) => {
      acknowledge();
      dismissStatus(EMAIL_STATUS_TOAST_ID, 'action');
      const state: ReviewHighlightState = { highlightEmailIds: ids };
      navigate('/review', { state });
    },
    [acknowledge, dismissStatus, navigate],
  );

  const tryAgain = useCallback(async () => {
    const { model: m, householdId: hh } = latest.current;
    if (!m || !hh || m.failedIds.length === 0) return;
    const ids = m.failedIds;
    setRetrying(true);
    setRetriedIds((prev) => new Set([...prev, ...ids]));
    try {
      await Promise.allSettled(
        ids.map((id) => emailService.retryEmail(id, hh)),
      );
      triggerProcessing().catch(() => {});
    } finally {
      await refresh();
      setRetrying(false);
    }
  }, [refresh]);

  const runAction = useCallback(
    (action: RunAction) => {
      const m = latest.current.model;
      if (!m) return;
      switch (action) {
        case 'review': {
          const rows = latest.current.emails;
          const withNews = m.emailIds.filter((id) => {
            const row = rows.find((r) => r.id === id);
            return row && (row.itemCount > 0 || row.status === 'ERROR');
          });
          openReview(withNews.length > 0 ? withNews : m.emailIds);
          return;
        }
        case 'see-email':
        case 'see-emails':
          openReview(m.failedIds);
          return;
        case 'try-again':
          void tryAgain();
          return;
        case 'upgrade-plan':
          acknowledge();
          dismissStatus(EMAIL_STATUS_TOAST_ID, 'action');
          navigate('/settings/plan');
          return;
      }
    },
    [acknowledge, dismissStatus, navigate, openReview, tryAgain],
  );

  const visible = resolved && canReview && !gmailBanner && model !== null;

  // Re-arm the slot's clock only when what the toast SAYS changes; a
  // re-render with the same words keeps the remaining visible time.
  const shownKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!visible || !model) {
      if (shownKeyRef.current !== null) {
        shownKeyRef.current = null;
        dismissStatus(EMAIL_STATUS_TOAST_ID, 'dismissed');
      }
      return;
    }
    const key = [
      model.state,
      model.headline,
      model.secondLine ?? '',
      model.emailIds.join(','),
    ].join('|');
    const changed = key !== shownKeyRef.current;
    shownKeyRef.current = key;
    const actions = model.actions
      .filter((a) => !(a === 'try-again' && retrying))
      .map((a) => ({ label: ACTION_LABELS[a], onClick: () => runAction(a) }));
    showStatus({
      id: EMAIL_STATUS_TOAST_ID,
      announcement: model.headline,
      durationMs: changed ? model.autoDismissMs : undefined,
      render: () => (
        <ToastStatus
          icon={model.icon}
          headline={model.headline}
          secondLine={model.secondLineSegments}
          actions={actions}
          onClose={() => {
            acknowledge();
            dismissStatus(EMAIL_STATUS_TOAST_ID, 'close');
          }}
        />
      ),
      onEnd: (reason) => {
        // A result that ran its visible time is seen. Our own programmatic
        // hides ('dismissed') are not.
        if (reason === 'timeout') acknowledge();
        if (reason !== 'replaced') shownKeyRef.current = null;
      },
    });
  }, [
    visible,
    model,
    retrying,
    showStatus,
    dismissStatus,
    acknowledge,
    runAction,
  ]);

  // Leave the slot clean on unmount (sign-out, household switch).
  useEffect(
    () => () => dismissStatus(EMAIL_STATUS_TOAST_ID, 'dismissed'),
    [dismissStatus],
  );

  return null;
}
